import { add, dist, scale, sub } from "../../math.ts";
import { Btn } from "../../net/input.ts";
import { ActionKind, MoveKind, hv, setHv } from "../../sim/components.ts";
import { aim, applyDamage, applyImpulse, applyStatus, coneConstructs, coneTargets, damageBarrier, isEnemy, lockOn, playersInRadius } from "../../sim/combat.ts";
import { damageConstruct } from "../../sim/constructs.ts";
import { HeroId, Role, Slot, type HeroDef } from "../types.ts";
import { fxk, pk, type HeroModule } from "../module.ts";
import { action, climb, commonInput, consume, launchProjectile, ownerAlive, ready, secs, simpleProjectile, swing, trigger, useUlt } from "../kitlib.ts";

export const V = { tracerNet: 0, climbing: 1, spinHits: 2 } as const;
export const PK = { CiderCluster: pk(HeroId.CiderMan, 0) } as const;
export const FX = { Web: fxk(HeroId.CiderMan, 0), Spin: fxk(HeroId.CiderMan, 1) } as const;

const def: HeroDef = {
  id: HeroId.CiderMan,
  slug: "cider-man",
  name: "Cider-Man",
  role: Role.Damage,
  color: "#e04b3a",
  blurb: "Acrobatic diver who marks a target, zips to it, uppercuts, and swings out.",
  health: 250,
  armor: 0,
  shields: 100,
  radius: 0.38,
  height: 1.75,
  headRadius: 0.22,
  speed: 6,
  jumpSpeed: 7.45,
  ammo: 0,
  reloadTime: 0,
  ultCost: 2800,
  abilities: {
    [Slot.Primary]: { name: "Orchard Knuckles", key: "LMB", description: "Fast close punches and kicks. Marked enemies trigger bonus damage.", cooldown: 0 },
    [Slot.Secondary]: { name: "Cider Clump", key: "RMB", description: "Fire a fast sticky shot that marks an enemy for 3 seconds.", cooldown: 2, charges: 5 },
    [Slot.Ability1]: { name: "Vine Swing", key: "Shift (hold)", description: "Swing from terrain. Stores up to 3 charges.", cooldown: 6, charges: 3 },
    [Slot.Ability2]: { name: "Yank the Core", key: "E", description: "Pull an unmarked enemy toward you, or zip to a marked one and kick them.", cooldown: 8 },
    [Slot.Ability3]: { name: "Pop-Up Combo", key: "F", description: "Uppercut nearby enemies, launching them upward. Stores two charges.", cooldown: 6, charges: 2 },
    [Slot.Ultimate]: { name: "Cider Cyclone", key: "Q", description: "Spin for 2.4 seconds, damaging and slowing nearby enemies.", cooldown: 0 },
  },
  passives: [
    { name: "Wall Sip", description: "Hold Space while pushing into a wall to crawl upward." },
    { name: "Orchard Sense", description: "Marked targets stay visible on your HUD and empower your next dive." },
  ],
};

function mark(sim: Parameters<HeroModule["kit"]["tick"]>[0], owner: number, target: number) {
  applyStatus(sim, target, "marked", 3, { source: owner, by: owner });
  setHv(sim.c, owner, V.tracerNet, sim.c.Net.id[target]!);
}

function markedByMe(sim: Parameters<HeroModule["kit"]["tick"]>[0], owner: number, target: number) {
  return sim.c.Status.marked[target]! > 0 && sim.c.Status.markedBy[target] === owner + 1;
}

function maxHealth(sim: Parameters<HeroModule["kit"]["tick"]>[0], eid: number) {
  return sim.c.Health.maxHp[eid]! + sim.c.Health.maxArmor[eid]! + sim.c.Health.maxShield[eid]!;
}

const module: HeroModule = {
  def,
  kit: {
    init(sim, eid) {
      setHv(sim.c, eid, V.tracerNet, 0);
    },
    tick(sim, eid, input, pressed, canAct) {
      const c = sim.c;
      const traced = Math.round(hv(c, eid, V.tracerNet));
      const tracedEid = traced ? sim.eidOf(traced) : undefined;
      if (tracedEid === undefined || !c.Player.alive[tracedEid] || !markedByMe(sim, eid, tracedEid)) setHv(c, eid, V.tracerNet, 0);

      if (c.Ult.active[eid]! > 0) {
        const center = add(sim.posOf(eid), { x: 0, y: 1.0, z: 0 });
        if (c.Ult.active[eid]! % secs(0.08) === 0) {
          for (const { eid: t } of playersInRadius(sim, center, 8, (e) => isEnemy(sim, eid, e), false)) {
            applyDamage(sim, t, 15, eid);
            const hits = hv(c, t, V.spinHits) + 1;
            setHv(c, t, V.spinHits, hits);
            applyStatus(sim, t, "slow", 1, { slowPct: Math.min(0.6, hits * 0.03), source: eid });
            if (hits >= 20) applyStatus(sim, t, "stun", 1.5, { source: eid });
          }
        }
        if (c.Ult.active[eid]! <= 1) setHv(c, eid, V.spinHits, 0);
      }

      commonInput(sim, eid, input, pressed);
      if (!canAct) return;

      if (useUlt(sim, eid, input, pressed)) {
        c.Ult.active[eid] = secs(2.4);
        c.Health.over[eid] = Math.max(c.Health.over[eid]!, maxHealth(sim, eid));
        c.Health.overTicks[eid] = secs(2.4);
        action(sim, eid, ActionKind.Ult, secs(2.4));
        sim.emit({ type: "explosion", pos: add(sim.posOf(eid), { x: 0, y: 1, z: 0 }), radius: 8, kind: FX.Spin });
      }

      if ((pressed & Btn.Ability1) && ready(sim, eid, Slot.Ability1) && swing(sim, eid, input, Btn.Ability1, { range: 30, seconds: 2.4 })) consume(sim, eid, Slot.Ability1);

      if ((pressed & Btn.Ability2) && ready(sim, eid, Slot.Ability2)) {
        const t = lockOn(sim, eid, 24, 0.33, false);
        if (t !== undefined) {
          consume(sim, eid, Slot.Ability2);
          action(sim, eid, ActionKind.Cast, 14);
          if (markedByMe(sim, eid, t)) {
            const p = add(sim.posOf(t), { x: 0, y: sim.hero(t).height * 0.5, z: 0 });
            c.Move.kind[eid] = MoveKind.Leap;
            c.Move.ticks[eid] = secs(0.45);
            c.Move.speed[eid] = 34;
            c.Move.x[eid] = p.x;
            c.Move.y[eid] = p.y;
            c.Move.z[eid] = p.z;
            c.Move.target[eid] = t + 1;
            applyDamage(sim, t, 55, eid, { pos: p });
            applyImpulse(sim, t, scale(sub(p, aim(sim, eid).origin), 1 / Math.max(1, dist(p, aim(sim, eid).origin))));
          } else {
            applyDamage(sim, t, 25, eid);
            applyImpulse(sim, t, scale(sub(sim.posOf(eid), sim.posOf(t)), 4 / Math.max(1, dist(sim.posOf(eid), sim.posOf(t)))));
          }
        }
      }

      if ((pressed & Btn.Ability3) && ready(sim, eid, Slot.Ability3)) {
        consume(sim, eid, Slot.Ability3);
        action(sim, eid, ActionKind.Cast, 16);
        for (const t of coneTargets(sim, eid, 4, 0.9, (e) => isEnemy(sim, eid, e))) {
          applyDamage(sim, t, markedByMe(sim, eid, t) ? 115 : 70, eid);
          c.Vel.y[t] = Math.max(c.Vel.y[t]!, 9);
          c.Player.grounded[t] = 0;
        }
        for (const { eid: t } of coneConstructs(sim, eid, 4, 0.9)) damageConstruct(sim, t, 70, eid);
      }

      if ((pressed & Btn.Secondary) && ready(sim, eid, Slot.Secondary)) {
        consume(sim, eid, Slot.Secondary);
        action(sim, eid, ActionKind.AltFire, 8);
        launchProjectile(sim, eid, PK.CiderCluster, 120, input, { radius: 0.12, seconds: 0.7 });
      }

      if (trigger(sim, eid, !!(input.buttons & Btn.Primary), true)) {
        c.Weapon.fireCd[eid] = secs(0.37);
        action(sim, eid, ActionKind.Fire, 6);
        let hit = false;
        for (const t of coneTargets(sim, eid, 3.2, 0.75, (e) => isEnemy(sim, eid, e))) {
          hit = true;
          applyDamage(sim, t, markedByMe(sim, eid, t) ? 70 : 25, eid);
          break;
        }
        if (!hit) for (const { eid: t } of coneConstructs(sim, eid, 3.2, 0.75).slice(0, 1)) damageConstruct(sim, t, 25, eid);
      }
    },
    movement(sim, eid, input, mods) {
      const swinging = swing(sim, eid, input, Btn.Ability1, { range: 30, seconds: 2.4 });
      const climbing = !swinging && !!(input.buttons & Btn.Jump) && climb(sim, eid, input, mods, { speed: input.buttons & Btn.Primary ? 9 : 3 });
      setHv(sim.c, eid, V.climbing, climbing ? 1 : 0);
    },
  },
  projectiles: {
    [PK.CiderCluster]: simpleProjectile(
      (sim, _p, owner, hit) => {
        if (!ownerAlive(sim, owner)) return;
        if (hit.target !== undefined) {
          applyDamage(sim, hit.target, 30, owner, { pos: hit.pos });
          mark(sim, owner, hit.target);
        } else if (hit.barrier !== undefined) damageBarrier(sim, hit.barrier, 30, owner);
        else if (hit.construct !== undefined) damageConstruct(sim, hit.construct, 30, owner);
        else sim.emit({ type: "impact", pos: hit.pos, kind: FX.Web });
      },
      { color: "#f7f7ff", size: 0.12, trail: true },
    ),
  },
  fx: {
    [FX.Web]: { color: "#f7f7ff" },
    [FX.Spin]: { color: "#e04b3a" },
  },
};

export default module;
